/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {EventCodeValueType, getEventName} from '@ion/core/src/services/EventCodes';
import {BaseAsset} from '@ion/core/src/types/entities';
import {getEventSpeed} from '@ion/core/src/units/helpers';
import {isTrue} from '@ion/core/src/utils/BooleanUtils';
import MiniVideoPlayer from '../../components/videoplayer/MiniVideoPlayer';
import {useIsPhone} from '../../hooks/useMedia';
import {EventHistoryBadgeIcon} from '../../icons/EventHistoryBadgeIcon';
import {EventHistoryLocationIcon} from '../../icons/EventHistoryLocationIcon';
import {EventHistorySpeedIcon} from '../../icons/EventHistorySpeedIcon';
import {EventHistoryTimeIcon} from '../../icons/EventHistoryTimeIcon';
import Render from '../../render/Render';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {DLSTheme} from '../../types/types';
import {
  formatAddress,
  formatSpeed,
  getFormattedDate,
  resolveValue
} from '../../utils/ComponentUtils';
import {navigateToGoogleStreetView} from '../helpers/globeUtils';

;

export interface AssetEventPopupContentProps {
  selectedAsset: BaseAsset;
  hasIOnVision?: boolean;
  skipSpeed?: boolean;
  setIONVision?: Function;
  streetViewClick?: Function | null;
}

/*
 * A row in the popup body. A field carrying an `onClick` renders as a button
 * rather than static text - only the address row does, opening Street View.
 */
interface PopupField {
  icon: JSX.Element;
  value: string;
  onClick?: () => void;
}

const ITEM_ROW = {
  display      : 'flex',
  alignItems   : 'center',
  marginBottom : '5px'
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  popup : {
    width : '100%'
  },
  header : {
    ...dlsAlt('typography', 'cta2'),
    backgroundColor : ({isSevere}: {isSevere: boolean}) => (
      isSevere ? dlsColor('caRed') : dlsColor('grey3')
    ),
    color        : dlsColor('white'),
    borderRadius : '4px 4px 0px 0px',
    display      : 'flex',
    alignItems   : 'center',
    minHeight    : '24px',
    width        : '100%',
    padding      : '2px 10px'
  },
  content : {
    padding : '12px'
  },
  item       : ITEM_ROW,
  /*
   * The address row doubles as the Street View trigger, so it renders as a
   * button reset back down to the plain row it sits alongside.
   */
  actionItem : {
    ...ITEM_ROW,
    width            : '100%',
    padding          : 0,
    border           : 'none',
    background       : 'none',
    textAlign        : 'left',
    cursor           : 'pointer',
    '&:hover $value' : {
      color          : dlsColor('caBlue'),
      textDecoration : 'underline'
    }
  },
  icon : {
    width          : '20px',
    display        : 'flex',
    alignItems     : 'center',
    justifyContent : 'center',
    marginRight    : '5px'
  },
  value : {
    ...dlsAlt('typography', 'label3'),
    maxWidth   : ({isPhone}: {isPhone: boolean}) => isPhone ? '178px' : '255px',
    wordWrap   : 'break-word',
    fontWeight : 500,
    lineHeight : '18px',
    color      : dlsColor('grey2')
  }
}));

/**
 * @AssetEventPopupContent
 *
 * The asset Event Popup provides a component which allows users to show some
 * HVS/FM asset information when hovering on an event on the History Layer.
 *
 *
 * ```typescript
 *
 *  <AssetEventPopupContent asset={selectedAsset} />
 *
 * ```
 */
export const AssetEventPopupContent = ({
  selectedAsset,
  hasIOnVision,
  setIONVision,
  streetViewClick,
  skipSpeed = false
}: AssetEventPopupContentProps) => {
  const {
    isSevereEvent,
    eventCode,
    assetName,
    eventTime,
    VideoURL,
    street,
    city,
    state
  } = selectedAsset || {};
  const isSevere = isTrue(isSevereEvent);
  const isPhone  = useIsPhone();
  const classes  = useStyles({isSevere, isPhone});

  const eventName = useMemo(() => (
    getEventName(
      {from: EventCodeValueType.eventCode, val: eventCode}
    )
  ), [eventCode]);

  const speedVal = useMemo(() => {
    const {speed, units} = getEventSpeed(selectedAsset);
    return formatSpeed(speed, units);
  }, [selectedAsset]);

  /*
   * Hands the event to the consumer when it has a Street View dialog mounted,
   * and otherwise keeps the previous behaviour of opening Google Maps.
   */
  const onStreetView = useCallback(function() {
    if (streetViewClick) {
      streetViewClick(selectedAsset);
    }
    if (!streetViewClick) {
      navigateToGoogleStreetView(selectedAsset);
    }
  }, [selectedAsset, streetViewClick]);

  const fields = useMemo(() => {
    const baseFields: PopupField[] = [
      {
        icon  : <EventHistoryBadgeIcon />,
        value : assetName
      },
      {
        icon  : <EventHistoryTimeIcon />,
        value : getFormattedDate(eventTime)
      },
      {
        icon    : <EventHistoryLocationIcon />,
        value   : formatAddress({street, city, state}),
        onClick : onStreetView
      }
    ];

    if (!skipSpeed) {
      baseFields.push({
        icon  : <EventHistorySpeedIcon />,
        value : resolveValue(speedVal)
      });
    }
    return baseFields;
  }, [
    assetName,
    eventTime,
    onStreetView,
    skipSpeed,
    speedVal,
    street,
    state,
    city
  ]);

  return (
    <div className={classes.popup}>
      <div className={classes.header}>
        {eventName}
      </div>
      {!isNil(VideoURL) && hasIOnVision && (
        <MiniVideoPlayer urls={[VideoURL]}
          handleOpenModal={function() {
            setIONVision(selectedAsset);
          }}
          isEventPopup
          isMiniPlayer
          audio
          isControls />
      )}
      <div className={classes.content}>
        {fields.map(function({icon, value, onClick}, index) {
          const body = (
            <React.Fragment>
              <Render.If condition={icon}>
                <span className={classes.icon}>
                  {icon}
                </span>
              </Render.If>
              <span className={classes.value}>
                {value}
              </span>
            </React.Fragment>
          );
          return (
            <Render key={`${index}-${value}`}>
              <Render.If condition={Boolean(onClick)}>
                <button type='button'
                  className={classes.actionItem}
                  aria-label={translate('common:streetView')}
                  onClick={onClick}>
                  {body}
                </button>
              </Render.If>
              <Render.Else>
                <span className={classes.item}>
                  {body}
                </span>
              </Render.Else>
            </Render>
          );
        })}
      </div>
    </div>
  );
};

export default AssetEventPopupContent;
